import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/sections/Footer';
import { SEO } from '@/components/SEO';
import { ScrollReveal } from '@/components/ui/ScrollReveal';
import { Flame, Users, UtensilsCrossed, Wine } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/hooks/useLocale';

const featureIcons = [Flame, UtensilsCrossed, Users, Wine];

const BOOKING_FORM_URL =
  'https://rgradcpiwm98zpetuigzzghomf3fmzoq.forms.pienissimo.pro/?id=uGpnNl';

const Experiences = () => {
  const { t } = useTranslation(['experiences', 'seo']);
  const { locale } = useLocale();
  const expectItems = t('experiences:expect.items', { returnObjects: true }) as Array<{
    title: string;
    text: string;
  }>;

  const experienceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FoodEstablishment',
    name: t('experiences:structuredData.name'),
    description: t('experiences:structuredData.description'),
    url: `https://braceesapori.it${locale === 'en' ? '/en' : ''}/corean-experience`,
    servesCuisine: t('experiences:structuredData.cuisine'),
    inLanguage: locale,
  };

  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title={t('seo:experiences.title')}
        description={t('seo:experiences.description')}
        keywords={t('seo:experiences.keywords')}
        ogImage="/img/DSC09190 (2024-04-22T17_54_40.682).JPG"
        logicalPath="/corean-experience"
        structuredData={experienceStructuredData}
      />
      <Header />

      <section className="relative min-h-[60vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/DSC09190 (2024-04-22T17_54_40.682).JPG"
            alt={t('experiences:hero.imageAlt')}
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/70" />
        </div>

        <div className="relative z-10 flex min-h-[60vh] items-center px-6 py-24">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <ScrollReveal>
                <span className="mb-4 inline-block text-sm font-light uppercase tracking-[0.3em] text-primary">
                  {t('experiences:hero.label')}
                </span>
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <h1 className="mb-6 text-5xl font-bold text-foreground md:text-6xl lg:text-7xl">
                  {t('experiences:hero.title')}
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <p className="text-xl font-light leading-relaxed text-muted-foreground md:text-2xl">
                  {t('experiences:hero.subtitle')}
                </p>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="mb-16 text-center">
              <h2 className="mb-6 text-4xl font-semibold text-foreground md:text-5xl">{t('experiences:intro.title')}</h2>
              <p className="text-lg font-light leading-relaxed text-muted-foreground md:text-xl">{t('experiences:intro.text')}</p>
            </div>
          </ScrollReveal>

          <div className="mb-16 grid gap-8 md:grid-cols-2">
            <ScrollReveal delay={100}>
              <div className="rounded-lg border border-border bg-card p-8">
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                    <UtensilsCrossed className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground">{t('experiences:menus.sfizioso.title')}</h3>
                </div>
                <p className="text-lg font-light leading-relaxed text-muted-foreground">{t('experiences:menus.sfizioso.text')}</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <div className="rounded-lg border border-border bg-card p-8">
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                    <Wine className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground">{t('experiences:menus.intenditore.title')}</h3>
                </div>
                <p className="text-lg font-light leading-relaxed text-muted-foreground">{t('experiences:menus.intenditore.text')}</p>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal delay={300}>
            <div className="mb-16">
              <h3 className="mb-8 text-center text-3xl font-semibold text-foreground">{t('experiences:expect.title')}</h3>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {expectItems.map((item, index) => {
                  const Icon = featureIcons[index];
                  return (
                    <div key={item.title} className="text-center">
                      <div className="mb-4 flex justify-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20">
                          <Icon className="h-8 w-8 text-primary" />
                        </div>
                      </div>
                      <h4 className="mb-2 text-lg font-semibold text-foreground">{item.title}</h4>
                      <p className="text-sm font-light text-muted-foreground">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={400}>
            <div className="mb-16 rounded-lg border border-border bg-card p-8 md:p-12">
              <h3 className="mb-6 text-3xl font-semibold text-foreground">{t('experiences:unique.title')}</h3>
              <div className="space-y-4 text-lg font-light leading-relaxed text-muted-foreground">
                <p>{t('experiences:unique.p1')}</p>
                <p>{t('experiences:unique.p2')}</p>
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={500}>
            <div id="prenota" className="scroll-mt-24">
              <div className="mb-8 text-center">
                <h3 className="mb-4 text-3xl font-semibold text-foreground md:text-4xl">
                  {t('experiences:booking.title')}
                </h3>
                <p className="text-muted-foreground">{t('experiences:booking.subtitle')}</p>
              </div>

              <div className="relative w-full overflow-hidden rounded-lg border border-border bg-card">
                <iframe
                  src={BOOKING_FORM_URL}
                  className="h-[1500px] w-full border-0"
                  title={t('experiences:booking.iframeTitle')}
                  allow="clipboard-write"
                  loading="lazy"
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Experiences;
